export const getLetterColor = (string: string) => {
  let color = '#0d428f';
  if(string?.length > 0) {
    const letter = string[0].toLowerCase();
    const index = letter.charCodeAt(0) - 97;
    color = colorsByLetter[index];
  }

  return color;
}

export const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

export const rgbToHex = (c: any)=> {
  const hex = c.toString(16);
  return hex.length == 1 ? "0" + hex : hex;
}

/**
 * Calcule la luminance relative d'une couleur
 * @param color - Couleur au format hex (#ffffff) ou rgb (rgb(255,255,255))
 * @returns Luminance entre 0 (noir) et 1 (blanc)
 */
export const getLuminance = (color: string): number => {
  let rgb: { r: number; g: number; b: number } | null = null;
  
  // Convertir la couleur en RGB
  if (color.startsWith('#')) {
    rgb = hexToRgb(color);
  } else if (color.startsWith('rgb')) {
    const matches = color.match(/\d+/g);
    if (matches && matches.length >= 3) {
      rgb = {
        r: parseInt(matches[0]),
        g: parseInt(matches[1]),
        b: parseInt(matches[2])
      };
    }
  }
  
  if (!rgb) {
    return 0.5; // Valeur par défaut si la couleur n'est pas reconnue
  }
  
  // Normaliser les valeurs RGB (0-1)
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  
  // Appliquer la formule de luminance relative
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  
  return luminance;
}

/**
 * Détermine si une couleur est claire ou sombre
 * @param color - Couleur au format hex ou rgb
 * @param threshold - Seuil de luminance (défaut: 0.5)
 * @returns true si la couleur est claire, false si elle est sombre
 */
export const isLightColor = (color: string, threshold: number = 0.5): boolean => {
  const luminance = getLuminance(color);
  return luminance > threshold;
}

/**
 * Retourne la couleur de texte appropriée (noir ou blanc) selon la couleur de fond
 * @param backgroundColor - Couleur de fond au format hex ou rgb
 * @param lightTextColor - Couleur pour les fonds sombres (défaut: '#FFFFFF')
 * @param darkTextColor - Couleur pour les fonds clairs (défaut: '#000000')
 * @returns Couleur de texte appropriée
 */
export const getContrastTextColor = (
  backgroundColor: string, 
  lightTextColor: string = '#FFFFFF', 
  darkTextColor: string = '#000000'
): string => {
  return isLightColor(backgroundColor) ? darkTextColor : lightTextColor;
}



//Colors by letter of words (26)
export const colorsByLetter = [
  '#94ae43',
  '#108f1b',
  '#8f3f0d',
  '#d1c806',
  '#770d8f',
  '#ca233d',
  '#267361',
  '#dd2e7d',
  '#8f300d',
  '#5f8f0d',
  '#8f340d',
  '#320d8f',
  '#3a9785',
  '#430a4c',
  '#342a78',
  '#429a75',
  '#294e14',
  '#3076dc',
  '#301c50',
  '#680b19',
  '#2a2d30',
  '#c6a609',
  '#e47a00',
  'rgba(235,68,90,0.79)',
  '#646838',
  '#648f0d'
];

// Palette thème (réutilisée par le composant colors)
export const themeColors: string[] = [
  '#1f1f1f', '#313131', '#3f3f3f', '#545353',
  '#4a235a', '#5b2c6f', '#6c3483', '#7d3c98',
  '#8e53a8', '#b271ce',
  '#600343', '#721953', '#792a60', '#803f6b',
  '#e5017c', '#e51a85', '#dc3d93', '#f566b3',
  '#1b2631', '#212f3c', '#283747', '#2e4053',
  '#154360', '#1a5276', '#1f618d', '#2471a3',
  '#023f7c', '#1255ab', '#3366a9', '#4a74a8',
  '#0e6251', '#117864', '#148f77', '#17a589',
  '#186a3b', '#1d8348', '#239b56', '#28b463',
  '#a2071a', '#a1192b', '#a22d3c', '#b74755',
  '#ee4802', '#d05521', '#d37349', '#e78e69',
  '#c4c42e', '#bebe5f', '#cccc4b', '#ffcc00'
];

// Couleurs soutenues pour les notes (20 couleurs vives mais harmonieuses)
export const pastelColorsForNotes = [
  '#FF6B6B', // Rouge corail
  '#4ECDC4', // Turquoise
  '#45B7D1', // Bleu ciel
  '#96CEB4', // Vert menthe
  '#FFEAA7', // Jaune moutarde
  '#DDA0DD', // Prune
  '#98D8C8', // Vert émeraude
  '#F7DC6F', // Jaune doré
  '#BB8FCE', // Violet clair
  '#85C1E9', // Bleu acier
  '#F8C471', // Orange pêche
  '#82E0AA', // Vert lime
  '#F1948A', // Rose saumon
  '#7FB3D3', // Bleu ardoise
  '#D2B4DE', // Lavande
  '#A9DFBF', // Vert pomme
  '#F9E79F', // Jaune crème
  '#F5B7B1', // Rose poudré
  '#AED6F1', // Bleu pervenche
  '#D5DBDB'  // Gris bleuté
];
